import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadEmployee } from '../../employees/actions/load-employee';
import { loadOrg } from '../../organization/actions/load-org';
import { userSelector } from '../../reducers/current-user-selectors';
import { employeeSelector } from '../../reducers/employee-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import { createCommentTemplate } from '../actions/create-comment-template';
import { loadCommentTemplates } from '../actions/load-comment-templates';
import { updateCommentTemplate } from '../actions/update-comment-template';
import { commentTemplatesForEmployeesSelector } from '../comment-template-selectors';

import { InlineTemplateList } from '../../message-templates/components/inline-template-list';
import { EmployeeMessageTemplateParser } from '../../message-templates/parsers/employee-message-template-parser';

type InputProps = {
  orgId: number;
  employeeId: number;
  onSelect: (messageBody: string, template: CommentTemplate) => void;
};

const mapStateToProps = (state: any, { orgId, employeeId }: InputProps) => ({
  org: orgSelector(state, orgId),
  user: userSelector(state),
  employee: employeeSelector(state, employeeId),
  commentTemplates: commentTemplatesForEmployeesSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId, employeeId }: InputProps,
) => ({
  actions: {
    loadData: () => {
      dispatch(loadEmployee(employeeId));
      dispatch(loadOrg(orgId));
      dispatch(loadCommentTemplates(orgId, 'employee'));
    },
    updateCommentTemplate: (commentTemplateId: number, comment: CommentTemplateUpdateRequest) =>
      dispatch(updateCommentTemplate(commentTemplateId, comment, orgId)),
    createCommentTemplate: (comment: CommentTemplateCreateRequest) =>
      dispatch(createCommentTemplate(comment, orgId)),
    deleteCommentTemplate: (commentTemplateId: number) =>
      dispatch(
        updateCommentTemplate(
          commentTemplateId,
          {
            isActive: false,
          },
          orgId,
        ),
      ),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeCommentTemplates = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ orgId, org, commentTemplates, onSelect, actions, employee, user }: Props) => {
  useEffect(() => {
    actions.loadData();
  });

  if (!org || !employee || !commentTemplates) {
    return null;
  }

  const handleDelete = (comment: CommentTemplate) => {
    actions.deleteCommentTemplate(comment.commentTemplateId);
  };

  const handleSave = (commentTemplate: CommentTemplate) => {
    if (commentTemplate.commentTemplateId) {
      const { commentTemplateId } = commentTemplate;
      actions.updateCommentTemplate(commentTemplateId, { ...commentTemplate });
    } else {
      actions.createCommentTemplate({ ...commentTemplate, orgId, profileType: 'employee' });
    }
  };

  const parser = new EmployeeMessageTemplateParser({ user, org, employee });
  return (
    <InlineTemplateList
      templates={commentTemplates}
      parser={parser}
      onSave={handleSave}
      onSelect={onSelect}
      onDelete={handleDelete}
      addButtonText="Add Note Template +"
      useRichText
      recipientType="employee"
      templateType="comment"
    />
  );
});
